<template>
    <div>
        <rkm-area
            v-if="motivoAtendimento"
            title="Motivo do atendimento"
            no-margin-top
        >
            <span>{{ motivoAtendimento }}</span>
        </rkm-area>

        <rkm-area
            v-if="examesLaboratoriais.length > 0 || (showBoxIfEmpty && !motivoAtendimento)"
            title="Exames laboratoriais"
            no-margin-top
        >
            <rkm-table
                :fields="fields.tables.examesLaboratoriais"
                :items="examesLaboratoriais"
                hide-filters
                hide-pagination
            >
                <template v-slot:cell(procedimento.codigo)="{ value }">
                    {{ value | formatProcedimento }}
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            v-if="examesImagem.length > 0 || (showBoxIfEmpty && !motivoAtendimento)"
            title="Exames de imagem"
            no-margin-top
        >
            <rkm-table
                :fields="fields.tables.examesImagem"
                :items="examesImagem"
                hide-filters
                hide-pagination
            >
                <template v-slot:cell(procedimento.codigo)="{ value }">
                    {{ value | formatProcedimento }}
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            v-if="triagem || (showBoxIfEmpty && !motivoAtendimento)"
            title="Triagem"
            type="fieldset"
            no-margin-top
        >
            <rkm-area
                v-if="(triagem && triagem.sinaisVitais.length > 0) || (showBoxIfEmpty && !motivoAtendimento)"
                title="Sinais vitais"
                no-margin-top
            >
                <rkm-table
                    :fields="fields.tables.sinaisVitais"
                    :items="triagem ? triagem.sinaisVitais : []"
                    hide-filters
                    hide-pagination
                    tag="div"
                >
                    <template v-slot:head(pa)="row">
                        <span v-rkm-tooltip.hover title="Pressão arterial">{{row.label}}</span>
                    </template>
                    <template v-slot:head(temperatura)="row">
                        <span v-rkm-tooltip.hover title="Temperatura">{{row.label}}</span>
                    </template>
                    <template v-slot:head(respiracao)="row">
                        <span v-rkm-tooltip.hover title="Respiração">{{row.label}}</span>
                    </template>
                    <template v-slot:head(saturacao)="row">
                        <span v-rkm-tooltip.hover title="Saturação">{{row.label}}</span>
                    </template>
                    <template v-slot:head(intensidadeDor)="row">
                        <span v-rkm-tooltip.hover title="Intensidade da dor">{{row.label}}</span>
                    </template>

                    <template v-slot:cell(info)="row">
                        <rkm-icon
                            :id="`pronto-atendimento-sinal-vital-info-${row.item.id}`"
                            class="mr-2"
                            icon="info-circle"
                        />

                        <rkm-popover
                            :target="`pronto-atendimento-sinal-vital-info-${row.item.id}`"
                            triggers="hover"
                        >
                            <div>{{ row.item.centroCusto.setor.nome }}</div>
                            <div><rkm-icon icon="user" /> {{ row.item.lotacao.profissional.nome }} - {{ row.item.lotacao.ocupacao.nome }}</div>
                            <div>Class. risco: {{ row.item.classificacaoRisco.nome }}</div>
                        </rkm-popover>

                        {{ row.item.dataHora }}
                    </template>

                    <template v-slot:cell(pa)="row">
                        {{row.item.pa}} {{row.item.pa ? row.item.paUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(glicemia)="row">
                        {{row.item.glicemia}} {{row.item.glicemia ? row.item.glicemiaUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(temperatura)="row">
                        {{row.item.temperatura}} {{row.item.temperatura ? row.item.temperaturaUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(pulso)="row">
                        {{row.item.pulso}} {{row.item.pulso ? row.item.pulsoUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(respiracao)="row">
                        {{row.item.respiracao}} {{row.item.respiracao ? row.item.respiracaoUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(saturacao)="row">
                        {{row.item.saturacao}} {{row.item.saturacao ? row.item.saturacaoUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(intensidadeDor)="row">
                        {{row.item.intensidadeDor}} {{row.item.intensidadeDor ? row.item.intensidadeDorUnidadeMedidaSigla : null}}
                    </template>
                    <template v-slot:cell(cintura)="row">
                        {{row.item.cintura}} {{row.item.cintura ? row.item.cinturaUnidadeMedidaSigla : null}}
                    </template>
                </rkm-table>
            </rkm-area>

            <rkm-area
                v-if="(triagem && triagem.historicosEnfermagem.length > 0) || (showBoxIfEmpty && !motivoAtendimento)"
                title="Queixas do paciente"
                no-margin-top
            >
                <rkm-form-incremental-textarea
                    :items="triagem ? triagem.historicosEnfermagem : []"
                    readonly
                >
                    <template v-slot:row="{ item }">
                        <rkm-row>
                            <rkm-col cols="4">
                                <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                                <div>{{ item.lotacao.profissional.nome }}</div>
                                <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                                <div>{{ item.dataHora }}</div>
                            </rkm-col>

                            <rkm-col>
                                {{ item.descricao }}
                            </rkm-col>
                        </rkm-row>
                    </template>
                </rkm-form-incremental-textarea>
            </rkm-area>
        </rkm-area>

        <rkm-area
            v-if="prescricoesInternas.length > 0 || (showBoxIfEmpty && !motivoAtendimento)"
            title="Medicamentos prescritos"
            no-margin-top
        >
            <rkm-list
                :items="prescricoesInternas"
                hide-filters
                hide-pagination
            >
                <template v-slot="{ item }">
                    <rkm-row>
                        <rkm-col>
                            <span>
                                <strong>{{ item.nome }}</strong>
                                - {{item.quantidade.toString().replace('.', ',')}} {{item.unidadeMedidaSigla}} {{item.formaFarmaceuticaNome}}
                            </span>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row v-if="item.administracao">
                        <rkm-col>
                            {{ item.administracao }}
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <hr>
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col
                            v-for="(aplicacao, i) in item.aplicacoes"
                            :key="i"
                            cols="3"
                        >
                            <span>
                                <strong>{{ aplicacao.numero }}ª Aplicacao:</strong> {{ aplicacao.status.nome }}
                            </span>
                        </rkm-col>
                    </rkm-row>
                </template>
            </rkm-list>
        </rkm-area>

        <rkm-area
            v-if="solicitouReavaliacao || (showBoxIfEmpty && !motivoAtendimento)"
            title="Reavaliação"
            no-margin-top
        >
            {{ solicitouReavaliacao }}
        </rkm-area>

        <rkm-area
            v-if="servicosEnfermagem.length > 0 || (showBoxIfEmpty && !motivoAtendimento)"
            title="Serviços de enfermagem"
            no-margin-top
        >
            <rkm-table
                :fields="fields.tables.servicosEnfermagem"
                :items="servicosEnfermagem"
                hide-filters
                hide-pagination
            >
                <template v-slot:cell(procedimento.codigo)="{ value }">
                    {{ value | formatProcedimento }}
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            v-if="observacoesEnfermagem.length > 0 || (showBoxIfEmpty && !motivoAtendimento)"
            title="Observações de Serviços de Enfermagem"
            no-margin-top
        >
            <rkm-form-incremental-textarea
                :items="observacoesEnfermagem"
                readonly
            >
                <template v-slot:row="{ item }">
                    <rkm-row>
                        <rkm-col cols="4">
                            <div>Setor: {{ item.centroCusto.setor.nome }}</div>
                            <div>{{ item.lotacao.profissional.nome }}</div>
                            <div>Class. Risco: {{ item.classificacaoRisco.nome }}</div>
                            <div>{{ item.dataHora }}</div>
                        </rkm-col>

                        <rkm-col>
                            {{ item.descricao }}
                        </rkm-col>
                    </rkm-row>
                </template>
            </rkm-form-incremental-textarea>
        </rkm-area>
    </div>
</template>

<script>
import RkmFormIncrementalTextarea from '@/components/pronto-atendimento/components/base/FormIncrementalTextarea'

import formats from '@/filters/formats'

export default {
    components: { RkmFormIncrementalTextarea },
    computed: {
        fields() {

            return {
                tables: {
                    examesLaboratoriais: [
                        { key: 'exame.nome', label: 'Exame' },
                        { key: 'procedimento.codigo', label: 'Código', class: 'col-min' },
                        { key: 'procedimento.nome', label: 'Procedimento' },
                        { key: 'status.nome', label: 'Status' },
                    ],
                    examesImagem: [
                        { key: 'exame.nome', label: 'Exame' },
                        { key: 'procedimento.codigo', label: 'Código', class: 'col-min' },
                        { key: 'procedimento.nome', label: 'Procedimento' },
                        { key: 'status.nome', label: 'Status' },
                    ],
                    prescricoesInternas: [
                        { key: 'nome', label: 'Medicamento' },
                    ],
                    servicosEnfermagem: [
                        { key: 'procedimento.codigo', label: 'Código', class: 'col-min' },
                        { key: 'procedimento.nome', label: 'Nome' },
                        { key: 'status.nome', label: 'Status' },
                    ],
                    sinaisVitais: [
                        { key: 'info', label: 'Data / Hora', class: 'col-min' },
                        { key: 'pa', label: 'PA' },
                        { key: 'glicemia', label: 'Glicemia' },
                        { key: 'temperatura', label: 'Temp.' },
                        { key: 'pulso', label: 'Pulso' },
                        { key: 'respiracao', label: 'Resp.' },
                        { key: 'saturacao', label: 'SaO2' },
                        { key: 'intensidadeDor', label: 'Dor' },
                        { key: 'cintura', label: 'Cintura' },
                    ],
                }
            }
        }
    },
    filters: {
        ...formats,
    },
    props: {
        showBoxIfEmpty: { type: Boolean, default: false },
        motivoAtendimento: { type: String, default: '' },
        servicosEnfermagem: { type: Array, default: () => [] },
        observacoesEnfermagem: { type: Array, default: () => [] },
        prescricoesInternas: { type: Array, default: () => [] },
        examesLaboratoriais: { type: Array, default: () => [] },
        examesImagem: { type: Array, default: () => [] },
        solicitouReavaliacao: { type: String },
        triagem: { type: Object },
    }
}
</script>